import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { RoomViewModel } from 'models';

import { classNames } from 'uitk-react-utils';
import { UitkQueuedImage } from 'uitk-react-images';

import { DialogTriggerWrapper } from 'components/infosite/property-details/dialog-trigger-wrapper';
import { Lazy } from 'components/common/lazy/lazy';

export interface RoomPhotosProps {
  analytics?: AnalyticsStore;
  className?: string;
  isDateless?: boolean;
  position: string;
  room: RoomViewModel;
  routeStore?: RouteStore;
  trackingRfrr?: string;
  triggerId?: string;
  name?: string;
  avoidLazy?: boolean;
}

export interface RoomPhotosState {
  currentGalleryIndex: number;
}

export interface QueuedImageLoadedProps {
  src?: string;
  queuedImageClasses: string;
  children?: any;
}

@inject('routeStore', 'analytics')
export class RoomPhotos extends React.Component<RoomPhotosProps, RoomPhotosState> {
  constructor(props) {
    super(props);

    this.state = {
      currentGalleryIndex: 0,
    };

    this.updateIndex = this.updateIndex.bind(this);
  }

  public static defaultProps = {
    isDateless: false,
    trackingRfrr: '.Room.PhotoGallery',
    triggerId: 'infosite-gallery',
  };

  private updateIndex(index: number): void {
    if (index >= 0) {
      this.setState({
        currentGalleryIndex: index,
      });
    }
  }

  public render() {
    const { analytics, className, position, room, triggerId, trackingRfrr, name, isDateless, avoidLazy, routeStore } = this.props;
    const photos = room && room.photos;
    const galleryTriggerProps = {
      analytics,
      clickHandler: this.track3PIRoomInfo,
      triggerId,
      trackingRfrr,
      images: photos,
      displayImageCount: photos && photos.length > 1,
      id: `roomGallery${position}`,
      onIndexChange: this.updateIndex,
      currentIndex: this.state.currentGalleryIndex,
      galleryTitle: name,
      position,
      room,
      routeStore,
    };

    if (!photos || photos.length >= 0 === false) {
      return null;
    }

    return (
      <section className={`${className} uitk-cell room__gallery`}>
        <h4 className="is-visually-hidden">{room.roomPhotosA11yHeader}</h4>
        <Viewport>
          <ViewSmall>
            <>
              <QueuedImageWrapper photos={photos} avoidLazy={avoidLazy} />
              <DialogTriggerWrapper {...galleryTriggerProps} />
            </>
          </ViewSmall>
          <ViewMedium>
            <QueuedImageWrapper photos={photos} isDateless={isDateless} avoidLazy={avoidLazy}>
              <DialogTriggerWrapper additionalClasses="room__info-trigger" {...galleryTriggerProps} />
            </QueuedImageWrapper>
          </ViewMedium>
        </Viewport>
      </section>
    );
  }

  private track3PIRoomInfo = () => {
    const { analytics, room } = this.props;
    if (room && room.shouldTrack3piDialog) {
      const trackingDataFor3piDialogClick = {
        rfrr: 'RoomsAndRates.ShowRoomInformation.3PI',
        linkName: 'Open Room Info dialog 3PI',
      };

      if (analytics && typeof analytics.trackEvent === 'function') {
        analytics.trackEvent(trackingDataFor3piDialogClick.rfrr, trackingDataFor3piDialogClick.linkName, null);
      }
    }
  };
}

const QueuedImageWrapper = props => {
  const queuedImageClasses = classNames('uitk-cell room__media image-loader image-loader__no-scrim', {
    'all-image-fit-cover': props.isDateless,
  });

  if (props.avoidLazy) {
    return <QueuedImageLoaded src={props.photos[0].src} queuedImageClasses={queuedImageClasses} children={props.children} />;
  }

  return (
    <Lazy key={`lazy-${props.photos[0].src}`} loadOffset={{ top: 350, bottom: 350 }}>
      <Lazy.Placeholder key={`lazy-placeholder-${props.photos[0].src}`}>
        <UitkQueuedImage src="" useBackgroundImage={true} className={queuedImageClasses} />
      </Lazy.Placeholder>
      <Lazy.Loaded key={`lazy-loaded-${props.photos[0].src}`}>
        <QueuedImageLoaded src={props.photos[0].src} queuedImageClasses={queuedImageClasses} children={props.children} />
      </Lazy.Loaded>
    </Lazy>
  );
};

const QueuedImageLoaded: React.FunctionComponent<QueuedImageLoadedProps> = props => (
  <UitkQueuedImage useBackgroundImage={true} src={props.src} className={props.queuedImageClasses}>
    {props.children}
  </UitkQueuedImage>
);
